import { useMemo, useState, type ChangeEvent } from "react";
import { api } from "../../api/client";
import type { ProductDto, StaffSnapshot } from "../../api/types";
import { useAppState } from "../../state/appState";
import { useSession } from "../../state/session";
import { useToasts } from "../../state/toasts";
import { Empty, Modal, Toggle } from "../../ui/components";
import { exclusionLabel, money } from "../../ui/format";

type Category = StaffSnapshot["categories"][number];
type Group = ProductDto["modifiers"][number];
type Option = Group["options"][number];

type Editing =
  | { kind: "category"; value: Category | null }
  | { kind: "product"; value: ProductDto | null; categorySlug: string }
  | { kind: "group"; value: Group | null; productSlug: string }
  | { kind: "option"; value: Option | null; groupId: string }
  /** Группа, общая для всей категории: объёмы напитков и им подобное. */
  | { kind: "categoryGroup"; categorySlug: string; groupSlug: string; title: string }
  | null;

/**
 * Меню и стоп-лист.
 *
 * Экран построен на трёх правилах, иначе на реальном меню из сорока позиций
 * он превращается в стену одинаковых кнопок:
 *
 *   1. Список остаётся списком: состав товара раскрывается по требованию.
 *   2. Редактирование живёт в формах, а не в строках.
 *   3. Разрушающих действий в списке нет вообще — они внутри форм,
 *      где рядом видно, что именно удаляется.
 */
export function MenuPage() {
  const { snapshot } = useAppState();
  const { can } = useSession();
  const { run } = useToasts();

  const [editing, setEditing] = useState<Editing>(null);
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [opened, setOpened] = useState<Record<string, boolean>>({});

  const editable = can("menu.manage");

  const categories = useMemo(
    () => (snapshot?.categories ?? []).filter(category => !category.archived),
    [snapshot]
  );
  const products = useMemo(
    () => (snapshot?.products ?? []).filter(product => !product.archived),
    [snapshot]
  );

  const stopped = useMemo(() => collectStopped(categories, products), [categories, products]);

  /**
   * Группы, которые есть у нескольких блюд категории.
   *
   * Объём напитка заведён у каждого напитка своей группой — цена за больший
   * стакан у лимонада и у эспрессо разная. Но набор объёмов у категории один,
   * и править его по каждому напитку значит однажды пропустить один.
   */
  const sharedGroups = useMemo(() => {
    const byCategory = new Map<string, Map<string, { title: string; count: number }>>();

    for (const product of products) {
      const groups = byCategory.get(product.categoryId) ?? new Map();

      for (const group of product.modifiers) {
        const seen = groups.get(group.id);
        groups.set(group.id, { title: group.title, count: (seen?.count ?? 0) + 1 });
      }

      byCategory.set(product.categoryId, groups);
    }

    return new Map(
      [...byCategory].map(([category, groups]) => [
        category,
        [...groups].filter(([, group]) => group.count > 1).map(([slug, group]) => ({ slug, ...group }))
      ])
    );
  }, [products]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return products;

    return products.filter(product =>
      [product.title, product.description, ...product.modifiers.flatMap(group => group.options.map(o => o.title))]
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [products, query]);

  if (!snapshot) return <Empty>Загрузка данных…</Empty>;

  async function setAvailability(entity: string, id: string, available: boolean) {
    await run(() => api.patch("/api/admin/catalog/availability", { entity, id, available }));
  }

  const searching = query.trim().length > 0;

  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">Каталог</p>
          <h1>Меню и стоп-лист</h1>
        </div>
        <div className="row-actions">
          <input
            className="menu-search"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Найти товар или добавку"
            aria-label="Поиск по меню"
          />
          {editable ? (
            <button type="button" className="btn btn-sm" onClick={() => setEditing({ kind: "category", value: null })}>
              + Категория
            </button>
          ) : null}
        </div>
      </div>

      {stopped.length > 0 ? (
        <section className="panel stop-panel">
          <header>
            <div>
              <h2>В стоп-листе</h2>
              <p>Эти позиции сейчас нельзя заказать.</p>
            </div>
          </header>
          <div className="opt-list">
            {stopped.map(item => (
              <span className="opt off-item" key={`${item.entity}-${item.id}`}>
                {item.title}
                <small className="muted">{item.kind}</small>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => void setAvailability(item.entity, item.id, true)}
                >
                  Вернуть
                </button>
              </span>
            ))}
          </div>
        </section>
      ) : null}

      <div className="menu-tree">
        {categories.length === 0 ? <Empty>Категорий пока нет.</Empty> : null}

        {categories.map(category => {
          const items = filtered.filter(product => product.categoryId === category.id);
          if (searching && items.length === 0) return null;

          const isOpen = searching || !collapsed[category.id];

          return (
            <section className="cat-block" key={category.id}>
              <div className="cat-head">
                <button
                  type="button"
                  className="disclose"
                  aria-expanded={isOpen}
                  onClick={() => setCollapsed(current => ({ ...current, [category.id]: isOpen }))}
                >
                  <span className={`caret ${isOpen ? "open" : ""}`} aria-hidden="true" />
                  <h3>{category.title}</h3>
                </button>

                <span className="tiny muted">{items.length} поз.</span>

                <label className="switch-label">
                  <Toggle
                    checked={!category.hidden}
                    label={`Категория «${category.title}» в меню`}
                    onChange={next => void setAvailability("category", category.id, next)}
                  />
                  <span className="tiny muted">{category.hidden ? "скрыта" : "в меню"}</span>
                </label>

                {editable ? (
                  <div className="row-actions">
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => setEditing({ kind: "category", value: category })}
                    >
                      Изменить
                    </button>
                    {(sharedGroups.get(category.id) ?? []).map(group => (
                      <button
                        key={group.slug}
                        type="button"
                        className="btn btn-sm"
                        onClick={() =>
                          setEditing({
                            kind: "categoryGroup",
                            categorySlug: category.id,
                            groupSlug: group.slug,
                            title: group.title
                          })
                        }
                      >
                        {group.title} · все {group.count}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => setEditing({ kind: "product", value: null, categorySlug: category.id })}
                    >
                      + Товар
                    </button>
                  </div>
                ) : null}
              </div>

              {isOpen ? (
                items.length === 0 ? (
                  <p className="empty" style={{ margin: 12 }}>
                    В категории нет товаров.
                  </p>
                ) : (
                  items.map(product => (
                    <ProductRow
                      key={product.id}
                      product={product}
                      editable={editable}
                      expanded={Boolean(opened[product.id])}
                      onToggleExpand={() =>
                        setOpened(current => ({ ...current, [product.id]: !current[product.id] }))
                      }
                      onEdit={() => setEditing({ kind: "product", value: product, categorySlug: category.id })}
                      onEditGroup={group => setEditing({ kind: "group", value: group, productSlug: product.id })}
                      onAddGroup={() => setEditing({ kind: "group", value: null, productSlug: product.id })}
                      onEditOption={(option, groupId) => setEditing({ kind: "option", value: option, groupId })}
                      onAddOption={groupId => setEditing({ kind: "option", value: null, groupId })}
                      onAvailability={setAvailability}
                    />
                  ))
                )
              ) : null}
            </section>
          );
        })}
      </div>

      {editing?.kind === "category" ? (
        <CategoryForm category={editing.value} onClose={() => setEditing(null)} />
      ) : null}

      {editing?.kind === "product" ? (
        <ProductForm
          product={editing.value}
          categorySlug={editing.categorySlug}
          categories={categories}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {editing?.kind === "group" ? (
        <GroupForm group={editing.value} productSlug={editing.productSlug} onClose={() => setEditing(null)} />
      ) : null}

      {editing?.kind === "categoryGroup" ? (
        <CategoryGroupForm
          categorySlug={editing.categorySlug}
          groupSlug={editing.groupSlug}
          title={editing.title}
          products={products.filter(product => product.categoryId === editing.categorySlug)}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {editing?.kind === "option" ? (
        <OptionForm option={editing.value} groupId={editing.groupId} onClose={() => setEditing(null)} />
      ) : null}
    </>
  );
}

function collectStopped(categories: Category[], products: ProductDto[]) {
  return [
    ...categories
      .filter(category => category.hidden)
      .map(category => ({ entity: "category", id: category.id, title: category.title, kind: "категория" })),
    ...products
      .filter(product => !product.available)
      .map(product => ({ entity: "product", id: product.id, title: product.title, kind: "товар" })),
    ...products.flatMap(product =>
      product.modifiers.flatMap(group => [
        ...(group.available
          ? []
          : [
              {
                entity: "group",
                id: group.groupId,
                title: `${product.title} · ${group.title}`,
                kind: "группа"
              }
            ]),
        ...group.options
          .filter(option => !option.available)
          .map(option => ({
            entity: "option",
            id: option.optionId,
            title: `${product.title} · ${option.title}`,
            kind: option.exclusion ? "исключение" : "добавка"
          }))
      ])
    )
  ];
}

/**
 * Строка товара. Свёрнута по умолчанию: в списке видно название, цену
 * и доступность, а состав раскрывается только когда он нужен.
 */
function ProductRow({
  product,
  editable,
  expanded,
  onToggleExpand,
  onEdit,
  onEditGroup,
  onAddGroup,
  onEditOption,
  onAddOption,
  onAvailability
}: {
  product: ProductDto;
  editable: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onEditGroup: (group: Group) => void;
  onAddGroup: () => void;
  onEditOption: (option: Option, groupId: string) => void;
  onAddOption: (groupId: string) => void;
  onAvailability: (entity: string, id: string, available: boolean) => void;
}) {
  const groups = product.modifiers;
  const optionCount = groups.reduce((sum, group) => sum + group.options.length, 0);
  const stoppedInside =
    groups.filter(group => !group.available).length +
    groups.reduce((sum, group) => sum + group.options.filter(option => !option.available).length, 0);

  return (
    <article className={`prod-row ${product.available ? "" : "stopped"}`}>
      {product.imageUrl ? (
        <img className="thumb" src={product.imageUrl} alt="" />
      ) : (
        <div className="thumb">нет фото</div>
      )}

      <div className="prod-main">
        <div className="prod-title">
          <strong>{product.title}</strong>
          {product.badges.map(badge => (
            <span className="chip" key={badge}>
              {badge}
            </span>
          ))}
          {!product.available ? <span className="chip chip-cancelled">В стоп-листе</span> : null}
        </div>

        {product.description ? <p className="tiny muted">{product.description}</p> : null}

        <p className="tiny num">
          <strong>{money(product.price)}</strong>
          {product.oldPrice ? <s className="muted"> {money(product.oldPrice)}</s> : null}
          {product.weight ? <span className="muted"> · {product.weight}</span> : null}
        </p>

        {groups.length > 0 ? (
          <button type="button" className="disclose tiny" aria-expanded={expanded} onClick={onToggleExpand}>
            <span className={`caret ${expanded ? "open" : ""}`} aria-hidden="true" />
            {groups.length} гр. · {optionCount} вар.
            {stoppedInside > 0 ? <span className="chip chip-cancelled">отключено: {stoppedInside}</span> : null}
          </button>
        ) : (
          <p className="tiny muted">без вариантов и добавок</p>
        )}

        {expanded
          ? groups.map(group => (
              <div className="group-block" key={group.groupId}>
                <div className="group-line">
                  <strong className="tiny">{group.title}</strong>
                  <span className="tiny muted">
                    {group.required ? "обязательная" : "необязательная"} · выбор {group.min}–{group.max}
                  </span>
                  <Toggle
                    checked={group.available}
                    label={`Группа «${group.title}» доступна`}
                    onChange={next => onAvailability("group", group.groupId, next)}
                  />
                  {editable ? (
                    <button type="button" className="btn btn-sm" onClick={() => onEditGroup(group)}>
                      Изменить
                    </button>
                  ) : null}
                </div>

                <div className="opt-list">
                  {group.options.map(option => (
                    <span
                      className={`opt ${option.available ? "" : "off"} ${option.exclusion ? "excl" : ""}`}
                      key={option.optionId}
                    >
                      {option.exclusion ? exclusionLabel(option.title) : option.title}
                      {option.price ? ` +${money(option.price)}` : ""}
                      {option.maxQty > 1 ? ` · до ${option.maxQty}` : ""}
                      <Toggle
                        checked={option.available}
                        label={`«${option.title}» доступен`}
                        onChange={next => onAvailability("option", option.optionId, next)}
                      />
                      {editable ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => onEditOption(option, group.groupId)}
                        >
                          Изменить
                        </button>
                      ) : null}
                    </span>
                  ))}

                  {editable ? (
                    <button type="button" className="btn btn-sm" onClick={() => onAddOption(group.groupId)}>
                      + Вариант
                    </button>
                  ) : null}
                </div>
              </div>
            ))
          : null}

        {expanded && editable ? (
          <button type="button" className="btn btn-sm" onClick={onAddGroup} style={{ justifySelf: "start" }}>
            + Группа вариантов
          </button>
        ) : null}
      </div>

      <div className="prod-side">
        <label className="switch-label">
          <Toggle
            checked={product.available}
            label={`Товар «${product.title}» доступен`}
            onChange={next => onAvailability("product", product.id, next)}
          />
          <span className="tiny muted">{product.available ? "в меню" : "в стоп-листе"}</span>
        </label>

        {editable ? (
          <button type="button" className="btn btn-sm" onClick={onEdit}>
            Изменить
          </button>
        ) : null}
      </div>
    </article>
  );
}

/** Опасные действия живут внизу формы: там рядом видно, что именно удаляется. */
function DangerZone({ title, hint, action }: { title: string; hint: string; action: () => void }) {
  return (
    <div className="danger-zone">
      <div>
        <strong className="tiny">{title}</strong>
        <p className="tiny muted">{hint}</p>
      </div>
      <button type="button" className="btn btn-sm btn-danger" onClick={action}>
        {title}
      </button>
    </div>
  );
}

function CategoryForm({ category, onClose }: { category: Category | null; onClose: () => void }) {
  const { run } = useToasts();
  const [title, setTitle] = useState(category?.title ?? "");
  const [sortOrder, setSortOrder] = useState(String(category?.sort ?? 0));

  async function save() {
    const body = { title: title.trim(), sortOrder: Number(sortOrder) || 0 };
    const ok = await run(
      () =>
        category
          ? api.patch(`/api/admin/catalog/categories/${category.id}`, body)
          : api.post("/api/admin/catalog/categories", body),
      category ? "Категория сохранена" : "Категория создана"
    );
    if (ok) onClose();
  }

  async function archive() {
    if (!category) return;
    if (!confirm(`Убрать категорию «${category.title}» в архив? Её товары исчезнут из меню.`)) return;

    const ok = await run(
      () => api.post(`/api/admin/catalog/categories/${category.id}/archive`, { archived: true }),
      "Категория в архиве"
    );
    if (ok) onClose();
  }

  return (
    <Modal
      title={category ? `Категория «${category.title}»` : "Новая категория"}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={!title.trim()}>
            Сохранить
          </button>
        </>
      }
    >
      <label className="field">
        <span>Название</span>
        <input autoFocus value={title} onChange={event => setTitle(event.target.value)} />
      </label>
      <label className="field">
        <span>Порядок отображения</span>
        <input inputMode="numeric" value={sortOrder} onChange={event => setSortOrder(event.target.value)} />
      </label>

      {category ? (
        <DangerZone
          title="В архив"
          hint="Категория и её товары исчезнут из меню, но сохранятся в истории заказов."
          action={() => void archive()}
        />
      ) : null}
    </Modal>
  );
}

function ProductForm({
  product,
  categorySlug,
  categories,
  onClose
}: {
  product: ProductDto | null;
  categorySlug: string;
  categories: Category[];
  onClose: () => void;
}) {
  const { run, notify } = useToasts();
  const [form, setForm] = useState({
    categorySlug: product?.categoryId ?? categorySlug,
    title: product?.title ?? "",
    description: product?.description ?? "",
    price: String(product?.price ?? ""),
    oldPrice: product?.oldPrice ? String(product.oldPrice) : "",
    weight: product?.weight ?? "",
    composition: product?.composition ?? "",
    prepTimeMin: product?.prepTimeMin ? String(product.prepTimeMin) : "",
    outputKg: product?.outputKg ? String(product.outputKg) : "",
    sortOrder: String(product?.sortOrder ?? 0),
    imageUrl: product?.imageUrl ?? "",
    badges: product?.badges.join(", ") ?? "",
    gtins: product?.gtins.join("\n") ?? ""
  });
  const [marked, setMarked] = useState(product?.marked ?? false);
  const [uploading, setUploading] = useState(false);

  function update(key: keyof typeof form, value: string) {
    setForm(current => ({ ...current, [key]: value }));
  }

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await api.upload<{ url: string }>("/api/admin/catalog/images", file);
      update("imageUrl", result.url);
      notify("Изображение загружено", "ok");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось загрузить файл", "error");
    } finally {
      setUploading(false);
    }
  }

  async function save() {
    const price = Number(form.price);
    if (!Number.isFinite(price) || price < 0) {
      notify("Укажите корректную цену", "error");
      return;
    }

    const body = {
      categorySlug: form.categorySlug,
      title: form.title.trim(),
      description: form.description.trim(),
      price,
      oldPrice: form.oldPrice.trim() ? Number(form.oldPrice) : null,
      weight: form.weight.trim() || null,
      composition: form.composition.trim() || null,
      prepTimeMin: form.prepTimeMin.trim() ? Number(form.prepTimeMin) : null,
      outputKg: form.outputKg.trim() ? Number(form.outputKg) : null,
      sortOrder: Number(form.sortOrder) || 0,
      imageUrl: form.imageUrl.trim() || null,
      badges: form.badges
        .split(",")
        .map(badge => badge.trim())
        .filter(Boolean),
      marked,
      // Номера — по одному на строку или через запятую; всё, что не цифры, —
      // разделитель. Снятая маркировка забирает и номера: без неё они не нужны.
      gtins: marked ? form.gtins.split(/[^0-9]+/).filter(Boolean) : []
    };

    const ok = await run(
      () =>
        product
          ? api.patch(`/api/admin/catalog/products/${product.id}`, body)
          : api.post("/api/admin/catalog/products", body),
      product ? "Товар сохранён" : "Товар создан"
    );
    if (ok) onClose();
  }

  async function archive() {
    if (!product) return;
    if (!confirm(`Убрать «${product.title}» в архив?`)) return;

    const ok = await run(
      () => api.post(`/api/admin/catalog/products/${product.id}/archive`, { archived: true }),
      "Товар в архиве"
    );
    if (ok) onClose();
  }

  return (
    <Modal
      title={product ? `Товар «${product.title}»` : "Новый товар"}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={!form.title.trim() || uploading}
          >
            Сохранить
          </button>
        </>
      }
    >
      <label className="field">
        <span>Название</span>
        <input autoFocus value={form.title} onChange={event => update("title", event.target.value)} />
      </label>

      <label className="field">
        <span>Описание</span>
        <textarea value={form.description} onChange={event => update("description", event.target.value)} />
      </label>

      <div className="field-row">
        <label className="field">
          <span>Категория</span>
          <select value={form.categorySlug} onChange={event => update("categorySlug", event.target.value)}>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.title}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Цена, ₽</span>
          <input inputMode="numeric" value={form.price} onChange={event => update("price", event.target.value)} />
        </label>
        <label className="field">
          <span>Старая цена, ₽</span>
          <input inputMode="numeric" value={form.oldPrice} onChange={event => update("oldPrice", event.target.value)} />
        </label>
      </div>

      <div className="field-row">
        <label className="field">
          <span>Вес или объём</span>
          <input value={form.weight} onChange={event => update("weight", event.target.value)} placeholder="310 г" />
        </label>
        <label className="field">
          <span>Время приготовления, мин</span>
          <input
            inputMode="numeric"
            value={form.prepTimeMin}
            onChange={event => update("prepTimeMin", event.target.value)}
          />
        </label>
        <label className="field">
          <span>Порядок</span>
          <input inputMode="numeric" value={form.sortOrder} onChange={event => update("sortOrder", event.target.value)} />
        </label>
      </div>

      <label className="field">
        <span>Выход в готовом виде, кг</span>
        <input
          inputMode="decimal"
          value={form.outputKg}
          onChange={event => update("outputKg", event.target.value)}
          placeholder="0.320"
        />
        <small className="muted">
          Графа калькуляционной карты. Это вес готового блюда, а не сумма закладки: на гриле котлета теряет, и разница
          между ними — то, ради чего графа и стоит в форме.
        </small>
      </label>

      <label className="field">
        <span>Состав</span>
        <input value={form.composition} onChange={event => update("composition", event.target.value)} />
      </label>

      <label className="field">
        <span>Отметки через запятую</span>
        <input value={form.badges} onChange={event => update("badges", event.target.value)} placeholder="Хит, Новинка" />
      </label>

      <label className="inline-check">
        <input type="checkbox" checked={marked} onChange={event => setMarked(event.target.checked)} />
        Маркированный товар — касса потребует скан кода перед продажей
      </label>

      {marked ? (
        <label className="field">
          <span>GTIN с этикеток — по одному на строку</span>
          <textarea
            rows={3}
            value={form.gtins}
            onChange={event => update("gtins", event.target.value)}
            placeholder={"04600494693335\n04600494693342"}
          />
          <small className="muted">
            Под одной позицией бывает несколько марок, и у каждой свой номер: перечислите все. По номеру касса
            находит позицию, когда бутылку сканируют раньше, чем выбирают товар, и сверяет отсканированное с тем,
            что пробивает. Пусто — сверять не с чем, продажа идёт по одной проверке кода. Такой товар не продаётся
            на доставку к лавочке: сканировать там некому.
          </small>
        </label>
      ) : null}

      <div className="field">
        <span>Фотография</span>
        <div className="filters">
          {form.imageUrl ? <img className="thumb" src={form.imageUrl} alt="" /> : <div className="thumb">нет фото</div>}
          <input type="file" accept="image/*" onChange={upload} disabled={uploading} />
          {form.imageUrl ? (
            <button type="button" className="btn btn-sm" onClick={() => update("imageUrl", "")}>
              Убрать
            </button>
          ) : null}
        </div>
      </div>

      {product ? (
        <DangerZone
          title="В архив"
          hint="Товар исчезнет из меню, но останется в уже оформленных заказах."
          action={() => void archive()}
        />
      ) : null}
    </Modal>
  );
}

function GroupForm({ group, productSlug, onClose }: { group: Group | null; productSlug: string; onClose: () => void }) {
  const { run } = useToasts();
  const [title, setTitle] = useState(group?.title ?? "");
  const [required, setRequired] = useState(group?.required ?? false);
  const [minSelect, setMinSelect] = useState(String(group?.min ?? 0));
  const [maxSelect, setMaxSelect] = useState(String(group?.max ?? 1));

  async function save() {
    const body = {
      title: title.trim(),
      required,
      minSelect: Number(minSelect) || 0,
      maxSelect: Number(maxSelect) || 1
    };

    const ok = await run(
      () =>
        group
          ? api.patch(`/api/admin/catalog/groups/${group.groupId}`, body)
          : api.post(`/api/admin/catalog/products/${productSlug}/groups`, body),
      group ? "Группа сохранена" : "Группа создана"
    );
    if (ok) onClose();
  }

  async function remove() {
    if (!group) return;
    if (!confirm(`Удалить группу «${group.title}» со всеми вариантами? Это необратимо.`)) return;

    const ok = await run(() => api.delete(`/api/admin/catalog/groups/${group.groupId}`), "Группа удалена");
    if (ok) onClose();
  }

  return (
    <Modal
      title={group ? `Группа «${group.title}»` : "Новая группа вариантов"}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={!title.trim()}>
            Сохранить
          </button>
        </>
      }
    >
      <label className="field">
        <span>Название</span>
        <input autoFocus value={title} onChange={event => setTitle(event.target.value)} placeholder="Булочка, Добавки" />
      </label>

      <label className="inline-check">
        <input type="checkbox" checked={required} onChange={event => setRequired(event.target.checked)} />
        Обязательная группа — без выбора товар нельзя добавить в корзину
      </label>

      <div className="field-row">
        <label className="field">
          <span>Минимум вариантов</span>
          <input inputMode="numeric" value={minSelect} onChange={event => setMinSelect(event.target.value)} />
        </label>
        <label className="field">
          <span>Максимум вариантов</span>
          <input inputMode="numeric" value={maxSelect} onChange={event => setMaxSelect(event.target.value)} />
        </label>
      </div>

      <p className="tiny muted">
        Для обязательной группы минимум автоматически поднимется до одного. Если такая группа попадёт в стоп-лист,
        товар станет недоступен целиком.
      </p>

      {group ? (
        <DangerZone
          title="Удалить группу"
          hint="Удалится вместе со всеми вариантами. Если нужно временно убрать — выключите её в стоп-листе."
          action={() => void remove()}
        />
      ) : null}
    </Modal>
  );
}

/**
 * Правка общей группы сразу по всей категории.
 *
 * Объём напитка живёт у каждого напитка отдельно: доплата за больший стакан
 * у лимонада и у эспрессо разная, и одна группа на всех эту разницу стёрла бы.
 * А вот набор объёмов у категории один — и менять «0,4 л» на «0,3 л» по
 * семнадцати напиткам вручную значит однажды пропустить один и оставить его
 * с прежним стаканом, о чём узнают на кассе.
 *
 * Поэтому здесь правятся названия и состав вариантов, а цена остаётся у
 * каждого блюда своей, пока её не задали явно.
 */
function CategoryGroupForm({
  categorySlug,
  groupSlug,
  title,
  products,
  onClose
}: {
  categorySlug: string;
  groupSlug: string;
  title: string;
  products: ProductDto[];
  onClose: () => void;
}) {
  const { run } = useToasts();

  /** Варианты берутся у первого блюда: набор у категории общий. */
  const source = products.find(product => product.modifiers.some(group => group.id === groupSlug));
  const sourceGroup = source?.modifiers.find(group => group.id === groupSlug);

  const [groupTitle, setGroupTitle] = useState(sourceGroup?.title ?? title);
  const [rows, setRows] = useState(
    () => (sourceGroup?.options ?? []).map(option => ({ slug: option.id, title: option.title, price: "" }))
  );
  const [busy, setBusy] = useState(false);

  /**
   * Цены по блюдам: одинаковая или разная.
   *
   * Показывается, чтобы владелец видел, что перезапишет. «120 ₽» и «разная»
   * означают совсем разные последствия у одной и той же пустой строки.
   */
  function priceHint(slug: string): string {
    const prices = new Set<number>();

    for (const product of products) {
      const group = product.modifiers.find(item => item.id === groupSlug);
      const option = group?.options.find(item => item.id === slug);
      if (option) prices.add(option.price);
    }

    if (prices.size === 0) return "нет";
    if (prices.size === 1) return `${[...prices][0]} ₽ у всех`;
    return `разная: ${Math.min(...prices)}–${Math.max(...prices)} ₽`;
  }

  function update(index: number, patch: Partial<{ title: string; price: string }>) {
    setRows(current => current.map((row, position) => (position === index ? { ...row, ...patch } : row)));
  }

  async function save() {
    setBusy(true);

    const ok = await run(
      () =>
        api.put(`/api/admin/catalog/categories/${categorySlug}/groups/${groupSlug}`, {
          title: groupTitle.trim(),
          options: rows
            .filter(row => row.title.trim())
            .map(row => ({
              slug: row.slug,
              title: row.title.trim(),
              // Пустая цена не трогает то, что стоит у блюда.
              ...(row.price.trim() ? { price: Number(row.price) } : {})
            }))
        }),
      `«${groupTitle}» обновлены во всей категории`
    );

    setBusy(false);
    if (ok) onClose();
  }

  return (
    <Modal
      title={`${title} · вся категория`}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || rows.every(row => !row.title.trim())}
            onClick={() => void save()}
          >
            {busy ? "Сохраняем…" : "Сохранить"}
          </button>
        </>
      }
    >
      <label className="field">
        <span>Название группы</span>
        <input value={groupTitle} onChange={event => setGroupTitle(event.target.value)} maxLength={120} />
      </label>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Вариант</th>
              <th className="num">Доплата, ₽</th>
              <th>Сейчас</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.slug}>
                <td>
                  <input
                    value={row.title}
                    onChange={event => update(index, { title: event.target.value })}
                    maxLength={120}
                  />
                </td>
                <td className="num">
                  <input
                    inputMode="numeric"
                    value={row.price}
                    placeholder="не менять"
                    onChange={event => update(index, { price: event.target.value })}
                  />
                </td>
                <td className="muted tiny">{priceHint(row.slug)}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => setRows(current => current.filter((_, position) => position !== index))}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="row-actions">
        <button
          type="button"
          className="btn btn-sm"
          onClick={() =>
            setRows(current => [
              ...current,
              // Slug свой у категории: по нему вариант находится в каждом блюде.
              { slug: `option-${current.length + 1}-${Date.now().toString(36)}`, title: "", price: "" }
            ])
          }
        >
          + Вариант
        </button>
      </div>

      <p className="hint">
        Меняется у всех {products.length} позиций категории. Пустая доплата оставляет ту, что стоит у блюда:
        за больший стакан у лимонада и у эспрессо берут разное. Удалённый вариант исчезнет из меню, но в уже
        оформленных заказах останется — их состав хранится снимком.
      </p>
    </Modal>
  );
}

function OptionForm({ option, groupId, onClose }: { option: Option | null; groupId: string; onClose: () => void }) {
  const { run } = useToasts();
  const [title, setTitle] = useState(option?.title ?? "");
  const [price, setPrice] = useState(String(option?.price ?? 0));
  const [exclusion, setExclusion] = useState(option?.exclusion ?? false);
  const [maxQty, setMaxQty] = useState(String(option?.maxQty ?? 1));

  async function save() {
    const body = {
      title: title.trim(),
      price: exclusion ? 0 : Number(price) || 0,
      exclusion,
      maxQty: exclusion ? 1 : Number(maxQty) || 1
    };

    const ok = await run(
      () =>
        option
          ? api.patch(`/api/admin/catalog/options/${option.optionId}`, body)
          : api.post(`/api/admin/catalog/groups/${groupId}/options`, body),
      option ? "Вариант сохранён" : "Вариант добавлен"
    );
    if (ok) onClose();
  }

  async function remove() {
    if (!option) return;
    if (!confirm(`Удалить «${option.title}»? Это необратимо.`)) return;

    const ok = await run(() => api.delete(`/api/admin/catalog/options/${option.optionId}`), "Вариант удалён");
    if (ok) onClose();
  }

  return (
    <Modal
      title={option ? `Вариант «${option.title}»` : "Новый вариант"}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={!title.trim()}>
            Сохранить
          </button>
        </>
      }
    >
      <label className="field">
        <span>Название</span>
        <input autoFocus value={title} onChange={event => setTitle(event.target.value)} placeholder="Сыр, Без лука" />
      </label>

      <label className="inline-check">
        <input type="checkbox" checked={exclusion} onChange={event => setExclusion(event.target.checked)} />
        Это исключение ингредиента, а не добавка
      </label>

      {exclusion ? (
        <p className="tiny muted">Исключение всегда бесплатное и добавляется один раз.</p>
      ) : (
        <div className="field-row">
          <label className="field">
            <span>Доплата, ₽</span>
            <input inputMode="numeric" value={price} onChange={event => setPrice(event.target.value)} />
          </label>
          <label className="field">
            <span>Сколько раз можно взять</span>
            <input inputMode="numeric" value={maxQty} onChange={event => setMaxQty(event.target.value)} />
          </label>
        </div>
      )}

      {option ? (
        <DangerZone
          title="Удалить вариант"
          hint="Если нужно временно убрать — выключите его в стоп-листе, это обратимо."
          action={() => void remove()}
        />
      ) : null}
    </Modal>
  );
}
